import React, { ReactNode, createRef } from 'react';
import { render } from 'react-dom';
import MessageToast, { MessageProps } from './Message';

export interface MessageConfig extends Omit<MessageProps, 'root'> {
	getContainer?: () => HTMLElement;
}

export interface MessageInstance {
	destroy: () => void;
	update: (content: ReactNode) => void;
}

function message({
	type,
	content,
	duration,
	showClose,
	onClose,
	getContainer,
}: MessageConfig): MessageInstance {
	const root = document.createElement('div');
	const ref = createRef<any>();
	const container = typeof getContainer === 'function' ? getContainer() : document.body;
	container.appendChild(root);

	render(
		(
			<MessageToast
				ref={ref}
				root={root}
				type={type}
				content={content}
				duration={duration}
				showClose={showClose}
				onClose={onClose}
			/>
		),
		root
	);

	return {
		destroy() {
			if (ref.current) {
				ref.current.destroy();
			}
		},
		update(newContent: ReactNode) {
			if (ref.current) {
				ref.current.update(newContent);
			}
		},
	};
}

export type OmitMessageConfig = Omit<MessageConfig, 'type' | 'content'>;

const api: any = {
	open: message,
};

['success', 'info', 'warning', 'error'].forEach(type => {
	api[type] = (content: ReactNode, config?: OmitMessageConfig) => api.open({
		type,
		content,
		...config,
	});
});

export interface MessageApi {
	open(args: MessageConfig): MessageInstance;
	info(content: ReactNode, config?: OmitMessageConfig): MessageInstance;
	success(content: ReactNode, config?: OmitMessageConfig): MessageInstance;
	error(content: ReactNode, config?: OmitMessageConfig): MessageInstance;
	warning(content: ReactNode, config?: OmitMessageConfig): MessageInstance;
}

export default api as MessageApi;
